import { gql } from "@medbillai/generated-graphql-types";

import { CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION } from "@medbillai/schemas";

export const getIssueQueryVariables = (issueId: string, userId: string) => {
  return {
    id: issueId,
    benefitsSummarySchemaVersion: CURRENT_BENEFITS_SUMMARY_SCHEMA_VERSION,
    userId,
  };
};

export const getCaseActivationQueryVariables = (
  issueId: string,
  userId: string,
) => ({ id: issueId, userId });

export const getIssueIdByCaseRefQueryVariables = (
  caseRef: string,
  userId: string,
) => ({ caseRef, userId });

export const getIssueIdByCaseRefQuery = gql(/* GraphQL */ `
  query GetIssueIdByCaseRef($caseRef: String!, $userId: ID!) {
    issue__Issue(
      filter: { caseRef: { eq: $caseRef }, owner: { id: { eq: $userId } } }
    ) {
      id
    }
  }
`);

export const getCaseActivationQuery = gql(/* GraphQL */ `
  query GetCaseActivation($id: ID!, $userId: ID!) {
    issue__Issue(filter: {
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      id
      nateraIntake
      isEscalated
      # Carries the route stamp: a funnel case opened in chat mode is walked
      # through its checklist in the timeline, not at the hub.
      labels
      userVisibleEvents(
        filter: { eventType: { eq: PAYMENT_PREFERENCES_REQUESTED } }
        order: { sentAt: { dir: ASC } }
        first: 1
      ) {
        id
      }
    }
  }
`);

// Optional progress is separate so an API rollback cannot reject the case query.
export const getCaseDocumentStagesQuery = gql(/* GraphQL */ `
  query GetCaseDocumentStages($id: ID!, $userId: ID!) {
    issue__Issue(filter: { id: { eq: $id }, owner: { id: { eq: $userId } } }) {
      id
      userVisibleEvents(filter: { eventType: { eq: DOCUMENT_UPLOADED } }) {
        __typename
        id
        ... on issue__DocumentUploadedEvent_Type {
          document { id }
          documentStage {
            attemptId
            attemptStartedAt
            stage
            detail
            at
          }
        }
      }
    }
  }
`);

// Each event type's selection for the two narrow reads, copied from GetIssue,
// which keeps its own text. The reads write into GetIssue's cache entry, so a
// field they omitted would leave it incomplete; `queries.schema.test.ts` holds
// them equal. `@unmask` keeps the operation types inline, so a narrow row
// assigns to GetIssue's event type.
//
// Shared event fields stay inline: without `possibleTypes`, a fragment on
// `issue__Event` matches nothing.
//
// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseChatEventFragment = gql(/* GraphQL */ `
  fragment CaseChatEventFields on issue__ChatEvent_Type {
    chatBody
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseDocumentClassificationFailedEventFragment = gql(/* GraphQL */ `
  fragment CaseDocumentClassificationFailedEventFields on issue__DocumentClassificationFailedEvent_Type {
    classificationStatus
    classificationResolvedAt
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseHaAssignedEventFragment = gql(/* GraphQL */ `
  fragment CaseHaAssignedEventFields on issue__HAAssignedEvent_Type {
    assigneeProfile {
      name
    }
    assigneeAverageCaseRating
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseTemplateReleaseRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseTemplateReleaseRequestedEventFields on issue__TemplateReleaseRequestedEvent_Type {
    # Aliased for the same reason as the payment fields below: both
    # subtypes carry requestKey, and gel-graphql collapses same-named
    # fields across inline fragments into one projection.
    releaseRequestKey: requestKey
    release {
      id
      createdAt
      grantedAt
    }
    template {
      id
      templateType
      displayName
      description
      templateCounterpartyType
      organization {
        slug
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const casePlanSelectionEventFragment = gql(/* GraphQL */ `
  fragment CasePlanSelectionEventFields on issue__PlanSelectionEvent_Type {
    id
    selectedPlan {
      id
    }
    options {
      id
      planLabel
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseUserSelectionRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseUserSelectionRequestedEventFields on issue__UserSelectionRequestedEvent_Type {
    id
    stringMessage: message
    stringOptions: options
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseUserMultiSelectionRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseUserMultiSelectionRequestedEventFields on issue__UserMultiSelectionRequestedEvent_Type {
    id
    multiMessage: message
    multiOptions: options
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseUserInputRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseUserInputRequestedEventFields on issue__UserInputRequestedEvent_Type {
    id
    # message is shared with the sibling selection events; alias it so
    # gel-graphql doesn't collapse the projections to a single null.
    inputMessage: message
    fieldSpec
    answerValue
    skippable
    freeTextAllowed
    outcome
    resolvedAt
    isResolved
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseConnectAccountRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseConnectAccountRequestedEventFields on issue__ConnectAccountRequestedEvent_Type {
    id
    sentAt
    connectAccountMessage: chatBody
    endpointType
    buttonText
    showSkipButton
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const casePaymentPreferencesRequestedEventFragment = gql(/* GraphQL */ `
  fragment CasePaymentPreferencesRequestedEventFields on issue__PaymentPreferencesRequestedEvent_Type {
    # Aliased to disambiguate from BillPayRequestedEvent.declinedAt;
    # gel-graphql collapses same-named fields across inline fragments
    # to a single projection, returning null for the wrong subtype.
    paymentRequestKey: requestKey
    paymentPrefsConfirmedAt: confirmedAt
    feeAcknowledgedAt
    paymentPrefsDeclinedAt: declinedAt
    paymentPrefsPreAuthStatus: preAuthStatus
    cancelledAt
    caseType
    contract
    resolvedFeeCap
    feePercentSnapshot
    appliedPromoCode {
      code
    }
    seenAt
  }
`);

// The non-event issue fields GetIssue reads. A turn can change case state, so
// the tail refreshes them alongside the transcript.
// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseIssueFieldsFragment = gql(/* GraphQL */ `
  fragment CaseIssueFields on issue__Issue_Type {
    __typename
    id
    title
    openedAt
    closedAt
    caseType
    providerDetail {
      kind
    }
    benefitsQuestionType
    billDisputeType
    additionalDetails
    benefitsInclude
    benefitsPrice
    isEscalated
    completedPostEscalationActions
    pendingPostEscalationActions
    hasStaffParticipant
    benefitsCoverage
    totalBillDollarAmount
    providerName
    labels
    nateraIntake
  }
`);

// GetIssue's latest-event selection, so a narrow read refreshes them as it does.
// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseIssueLatestEventsFragment = gql(/* GraphQL */ `
  fragment CaseIssueLatestEvents on issue__Issue_Type {
    lastEvent {
      __typename
      id
      sentAt
      eventType
      participant
      ... on issue__ChatEvent_Type {
        chatBody
      }
      ... on issue__TemplateReleaseRequestedEvent_Type {
        template {
          description
        }
      }
      ... on issue__BillPaymentRequestedEvent_Type {
        acceptedAt
        billPaid
      }
      ... on issue__ClosurePaymentRequestedEvent_Type {
        dollarAmountSaved
        totalBillDollarAmount
        successFee
        chargeDelayDays
        sentAt
      }
      ... on issue__DocumentAnalysisEvent_Type {
        status
      }
    }
    lastNonUserEvent {
      __typename
      id
      participant
    }
    firstUnseenEvent {
      __typename
      id
      sentAt
      eventType
      participant
      ... on issue__ChatEvent_Type {
        chatBody
      }
      ... on issue__TemplateReleaseRequestedEvent_Type {
        template {
          description
        }
      }
      ... on issue__BillPaymentRequestedEvent_Type {
        acceptedAt
        billPaid
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseDocumentUploadedEventFragment = gql(/* GraphQL */ `
  fragment CaseDocumentUploadedEventFields on issue__DocumentUploadedEvent_Type {
    uploading @client
    uploadFailed @client
    previewPromptMessage @client
    document {
      id
      name
      dataType
      createdAt
      latestIngestion {
        endedAt
        resources(filter: { isLatest: { eq: true } }, first: 1) {
          __typename
          id
          ... on fhirext__InvoiceVersion_Type {
            invoiceTitle: title
          }
          ... on fhirext__ExplanationOfBenefitVersion_Type {
            eobTitle: title
          }
        }
      }
      files {
        id
        sizeBytes
        contentType
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseHipaaReleaseRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseHipaaReleaseRequestedEventFields on issue__HipaaReleaseRequestedEvent_Type {
    hipaaRelease {
      id
      name
      dateOfBirth
      createdAt
      grantedAt
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBillDisputeEventFragment = gql(/* GraphQL */ `
  fragment CaseBillDisputeEventFields on issue__BillDisputeEvent_Type {
    eobId
    healthRecordKind
    healthRecordKey
    eob {
      id
      resourceId
      provider {
        display
      }
      facility {
        display
      }
      serviceDate
      decision {
        text
        coding {
          code
        }
      }
      findings(
        filter: {
          and: [
            {
              explanation: { exists: true }
              lastReviewStatus: { eq: ACCEPTED }
              userDismissed: { eq: false }
            }
          ]
        }
      ) {
        id
        type
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseClosedEventFragment = gql(/* GraphQL */ `
  fragment CaseClosedEventFields on issue__ClosedEvent_Type {
    userClosureReason
    feedback {
      id
      rating
      review
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const casePaymentAgreementRequestedEventFragment = gql(/* GraphQL */ `
  fragment CasePaymentAgreementRequestedEventFields on issue__PaymentAgreementRequestedEvent_Type {
    userAccepted
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseClosurePaymentRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseClosurePaymentRequestedEventFields on issue__ClosurePaymentRequestedEvent_Type {
    dollarAmountSaved
    totalBillDollarAmount
    successFee
    chargeDelayDays
    sentAt
    chargeDate
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBenefitsSummaryGeneratedEventFragment = gql(/* GraphQL */ `
  fragment CaseBenefitsSummaryGeneratedEventFields on issue__BenefitsSummaryGeneratedEvent_Type {
    id
    sentAt
    plan {
      id
      planLabel
      link {
        id
        endpoint {
          id
          slug
        }
      }
      benefitsSummaryResults(
        filter: { schemaVersion: { eq: $benefitsSummarySchemaVersion } }
      ) {
        id
        summary
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseConnectInsuranceRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseConnectInsuranceRequestedEventFields on issue__ConnectInsuranceRequestedEvent_Type {
    id
    sentAt
    insuranceLinkMessage
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBillPayRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseBillPayRequestedEventFields on issue__BillPayRequestedEvent_Type {
    # Aliased to disambiguate from BillPaymentRequestedEvent.acceptedAt;
    # gel-graphql collapses same-named fields across inline fragments to
    # a single EdgeQL projection, returning null for the wrong subtype.
    billPayAcceptedAt: acceptedAt
    billPayPreAuthStatus: preAuthStatus
    declinedAt
    availableMinimumDiscountOptions
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBillPaySuccessEventFragment = gql(/* GraphQL */ `
  fragment CaseBillPaySuccessEventFields on issue__BillPaySuccessEvent_Type {
    negotiatedBillAmount
    originalBillAmount
    successFeeAmount
    successFeePercent
    totalCharged
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseCardCollectionRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseCardCollectionRequestedEventFields on issue__CardCollectionRequestedEvent_Type {
    cardSucceededAt
    cardSupersededAt
    # Aliased for the same reason as the payment fields above. Unaliased,
    # gel-graphql collapses it into one projection of a required property
    # and rejects the whole query for every event of another subtype.
    cardActionRequired: actionRequired
    seenAt
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseOutreachEventFragment = gql(/* GraphQL */ `
  fragment CaseOutreachEventFields on issue__OutreachEvent_Type {
    outreachBody
    attemptNumber
    askKind
    # The re-raised ask is rendered by the ask's own card component, so
    # this selection must mirror that ask's userVisibleEvents fragment
    # field for field (aliases included) or the delegation won't
    # typecheck.
    ask {
      __typename
      id
      eventType
      participant
      sentAt
      userVisible
      sentByProfile {
        name
      }
      eventFeedbacks(filter: { owner: { id: { eq: $userId } } }) {
        id
        rating
      }
      ... on issue__HipaaReleaseRequestedEvent_Type {
        hipaaRelease {
          id
          name
          dateOfBirth
          createdAt
          grantedAt
        }
      }
      ... on issue__TemplateReleaseRequestedEvent_Type {
        release {
          id
          createdAt
          grantedAt
        }
        template {
          id
          templateType
          displayName
          description
          templateCounterpartyType
          organization {
            slug
          }
        }
      }
      ... on issue__PaymentPreferencesRequestedEvent_Type {
        paymentRequestKey: requestKey
        paymentPrefsConfirmedAt: confirmedAt
        feeAcknowledgedAt
        paymentPrefsDeclinedAt: declinedAt
        paymentPrefsPreAuthStatus: preAuthStatus
        cancelledAt
        caseType
        contract
        resolvedFeeCap
        feePercentSnapshot
        appliedPromoCode {
          code
        }
        seenAt
      }
      ... on issue__CardCollectionRequestedEvent_Type {
        cardSucceededAt
        cardSupersededAt
        seenAt
      }
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBillPaymentRequestedEventFragment = gql(/* GraphQL */ `
  fragment CaseBillPaymentRequestedEventFields on issue__BillPaymentRequestedEvent_Type {
    billPaymentAcceptedAt: acceptedAt
    billPaid
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseBillPaymentSuccessEventFragment = gql(/* GraphQL */ `
  fragment CaseBillPaymentSuccessEventFields on issue__BillPaymentSuccessEvent_Type {
    stripePaymentIntentId
    billPaymentRequest {
      id
    }
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const casePaymentChargeFailedEventFragment = gql(/* GraphQL */ `
  fragment CasePaymentChargeFailedEventFields on issue__PaymentChargeFailedEvent_Type {
    paymentResolvedAt
    paymentResolvedReason
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const caseDocumentAnalysisEventFragment = gql(/* GraphQL */ `
  fragment CaseDocumentAnalysisEventFields on issue__DocumentAnalysisEvent_Type {
    status
    errorMessage
    errorIsRetriable
  }
`);

// biome-ignore lint/correctness/noUnusedVariables: registers the fragment with codegen; operations reference it by name.
const casePostEscalationActionsEventFragment = gql(/* GraphQL */ `
  fragment CasePostEscalationActionsEventFields on issue__PostEscalationActionsEvent_Type {
    actions(order: { isRequired: { dir: DESC } }) {
      __typename
      isRequired
      ... on issue__UploadBillPostEscalationAction_Type {
        uploadBillLinkedEvent: linkedEvent {
          document {
            id
            name
            files {
              id
              contentType
            }
          }
        }
      }
      ... on issue__UploadEobPostEscalationAction_Type {
        uploadEobLinkedEvent: linkedEvent {
          document {
            id
            name
            files {
              id
              contentType
            }
          }
        }
      }
      ... on issue__UploadMemberIdPostEscalationAction_Type {
        uploadMemberIdLinkedEvent: linkedEvent {
          document {
            id
            name
            files {
              id
              contentType
            }
          }
        }
      }
      ... on issue__UploadProofOfIncomePostEscalationAction_Type {
        uploadProofOfIncomeLinkedEvent: linkedEvent {
          document {
            id
            name
            files {
              id
              contentType
            }
          }
        }
      }
      ... on issue__SignTemplatePostEscalationAction_Type {
        signLinkedEvent: linkedEvent {
          id
          release {
            id
          }
          template {
            id
            templateType
            displayName
          }
        }
      }
      ... on issue__PaymentAgreementPostEscalationAction_Type {
        payLinkedEvent: linkedEvent {
          id
          userAccepted
        }
      }
      ... on issue__BillPayPostEscalationAction_Type {
        billPayLinkedEvent: linkedEvent {
          id
          acceptedAt
          declinedAt
          availableMinimumDiscountOptions
        }
      }
      ... on issue__ConnectInsurancePostEscalationAction_Type {
        insLinkedEvent: linkedEvent {
          id
          endpointType
        }
      }
      ... on issue__ConnectEhrPostEscalationAction_Type {
        ehrLinkedEvent: linkedEvent {
          id
          endpointType
        }
      }
      ... on issue__PaymentPreferencesPostEscalationAction_Type {
        paymentPrefsLinkedEvent: linkedEvent {
          id
          caseType
          contract
          requestKey
          resolvedFeeCap
          feePercentSnapshot
          cancelledAt
        }
      }
    }
  }
`);

export const getIssueQuery = gql(/* GraphQL */ `
  query GetIssue($id: ID!, $benefitsSummarySchemaVersion: Int!, $userId: ID!) {
    issue__Issue(filter: {
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      __typename
      id
      title
      openedAt
      closedAt
      caseType
      providerDetail {
        kind
      }
      benefitsQuestionType
      billDisputeType
      additionalDetails
      benefitsInclude
      benefitsPrice
      isEscalated
      completedPostEscalationActions
      pendingPostEscalationActions
      hasStaffParticipant
      benefitsCoverage
      totalBillDollarAmount
      providerName
      labels
      nateraIntake
      userVisibleEvents(order: { sentAt: { dir: ASC } }) {
        # __typename seems to be needed for the polymorphic query here.
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks (filter:  {owner: {id: {eq: $userId}}}) {
          id
          rating
        }

        ... on issue__DocumentUploadedEvent_Type {
          uploading @client
          uploadFailed @client
          previewPromptMessage @client
          document {
            id
            name
            dataType
            createdAt
            latestIngestion {
              endedAt
              resources(filter: { isLatest: { eq: true } }, first: 1) {
                __typename
                id
                ... on fhirext__InvoiceVersion_Type {
                  invoiceTitle: title
                }
                ... on fhirext__ExplanationOfBenefitVersion_Type {
                  eobTitle: title
                }
              }
            }
            files {
              id
              sizeBytes
              contentType
            }
          }
        }
        ... on issue__DocumentClassificationFailedEvent_Type {
          classificationStatus
          classificationResolvedAt
        }
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__HipaaReleaseRequestedEvent_Type {
          hipaaRelease {
            id
            name
            dateOfBirth
            createdAt
            grantedAt
          }
        }
        ... on issue__BillDisputeEvent_Type {
          eobId
          healthRecordKind
          healthRecordKey
          eob {
            id
            resourceId
            provider {
              display
            }
            facility {
              display
            }
            serviceDate
            decision {
              text
              coding {
                code
              }
            }
            findings(
              filter: {
                and: [
                  {
                    explanation: { exists: true }
                    lastReviewStatus: { eq: ACCEPTED }
                    userDismissed: { eq: false }
                  }
                ]
              }
            ) {
              id
              type
            }
          }
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          # Aliased for the same reason as the payment fields below: both
          # subtypes carry requestKey, and gel-graphql collapses same-named
          # fields across inline fragments into one projection.
          releaseRequestKey: requestKey
          release {
            id
            createdAt
            grantedAt
          }
          template {
            id
            templateType
            displayName
            description
            templateCounterpartyType
            organization {
              slug
            }
          }
        }
        ... on issue__ClosedEvent_Type {
          userClosureReason
          feedback {
            id
            rating
            review
          }
        }
        ... on issue__PaymentAgreementRequestedEvent_Type {
          userAccepted
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
          chargeDate
        }
        ... on issue__BenefitsSummaryGeneratedEvent_Type {
          id
          sentAt
          plan {
            id
            planLabel
            link {
              id
              endpoint {
                id
                slug
              }
            }
            benefitsSummaryResults(
              filter: { schemaVersion: { eq: $benefitsSummarySchemaVersion } }
            ) {
              id
              summary
            }
          }
        }
        ... on issue__PlanSelectionEvent_Type {
          id
          selectedPlan {
            id
          }
          options {
            id
            planLabel
          }
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          id
          stringMessage: message
          stringOptions: options
        }
        ... on issue__UserMultiSelectionRequestedEvent_Type {
          id
          multiMessage: message
          multiOptions: options
        }
        ... on issue__UserInputRequestedEvent_Type {
          id
          # message is shared with the sibling selection events; alias it so
          # gel-graphql doesn't collapse the projections to a single null.
          inputMessage: message
          fieldSpec
          answerValue
          skippable
          freeTextAllowed
          outcome
          resolvedAt
          isResolved
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          id
          sentAt
          insuranceLinkMessage
        }
        ... on issue__ConnectAccountRequestedEvent_Type {
          id
          sentAt
          connectAccountMessage: chatBody
          endpointType
          buttonText
          showSkipButton
        }
        ... on issue__BillPayRequestedEvent_Type {
          # Aliased to disambiguate from BillPaymentRequestedEvent.acceptedAt;
          # gel-graphql collapses same-named fields across inline fragments to
          # a single EdgeQL projection, returning null for the wrong subtype.
          billPayAcceptedAt: acceptedAt
          billPayPreAuthStatus: preAuthStatus
          declinedAt
          availableMinimumDiscountOptions
        }
        ... on issue__BillPaySuccessEvent_Type {
          negotiatedBillAmount
          originalBillAmount
          successFeeAmount
          successFeePercent
          totalCharged
        }
        ... on issue__PaymentPreferencesRequestedEvent_Type {
          # Aliased to disambiguate from BillPayRequestedEvent.declinedAt;
          # gel-graphql collapses same-named fields across inline fragments
          # to a single projection, returning null for the wrong subtype.
          paymentRequestKey: requestKey
          paymentPrefsConfirmedAt: confirmedAt
          feeAcknowledgedAt
          paymentPrefsDeclinedAt: declinedAt
          paymentPrefsPreAuthStatus: preAuthStatus
          cancelledAt
          caseType
          contract
          resolvedFeeCap
          feePercentSnapshot
          appliedPromoCode {
            code
          }
          seenAt
        }
        ... on issue__CardCollectionRequestedEvent_Type {
          cardSucceededAt
          cardSupersededAt
          # Aliased for the same reason as the payment fields above. Unaliased,
          # gel-graphql collapses it into one projection of a required property
          # and rejects the whole query for every event of another subtype.
          cardActionRequired: actionRequired
          seenAt
        }
        ... on issue__OutreachEvent_Type {
          outreachBody
          attemptNumber
          askKind
          # The re-raised ask is rendered by the ask's own card component, so
          # this selection must mirror that ask's userVisibleEvents fragment
          # field for field (aliases included) or the delegation won't
          # typecheck.
          ask {
            __typename
            id
            eventType
            participant
            sentAt
            userVisible
            sentByProfile {
              name
            }
            eventFeedbacks(filter: { owner: { id: { eq: $userId } } }) {
              id
              rating
            }
            ... on issue__HipaaReleaseRequestedEvent_Type {
              hipaaRelease {
                id
                name
                dateOfBirth
                createdAt
                grantedAt
              }
            }
            ... on issue__TemplateReleaseRequestedEvent_Type {
              release {
                id
                createdAt
                grantedAt
              }
              template {
                id
                templateType
                displayName
                description
                templateCounterpartyType
                organization {
                  slug
                }
              }
            }
            ... on issue__PaymentPreferencesRequestedEvent_Type {
              paymentRequestKey: requestKey
              paymentPrefsConfirmedAt: confirmedAt
              feeAcknowledgedAt
              paymentPrefsDeclinedAt: declinedAt
              paymentPrefsPreAuthStatus: preAuthStatus
              cancelledAt
              caseType
              contract
              resolvedFeeCap
              feePercentSnapshot
              appliedPromoCode {
                code
              }
              seenAt
            }
            ... on issue__CardCollectionRequestedEvent_Type {
              cardSucceededAt
              cardSupersededAt
              seenAt
            }
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          billPaymentAcceptedAt: acceptedAt
          billPaid
        }
        ... on issue__BillPaymentSuccessEvent_Type {
          stripePaymentIntentId
          billPaymentRequest {
            id
          }
        }
        ... on issue__PaymentChargeFailedEvent_Type {
          paymentResolvedAt
          paymentResolvedReason
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
          errorMessage
          errorIsRetriable
        }
        ... on issue__HAAssignedEvent_Type {
          assigneeProfile {
            name
          }
          assigneeAverageCaseRating
        }
        ... on issue__PostEscalationActionsEvent_Type {
          actions(order: { isRequired: { dir: DESC } }) {
            __typename
            isRequired
            ... on issue__UploadBillPostEscalationAction_Type {
              uploadBillLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__UploadEobPostEscalationAction_Type {
              uploadEobLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__UploadMemberIdPostEscalationAction_Type {
              uploadMemberIdLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__UploadProofOfIncomePostEscalationAction_Type {
              uploadProofOfIncomeLinkedEvent: linkedEvent {
                document {
                  id
                  name
                  files {
                    id
                    contentType
                  }
                }
              }
            }
            ... on issue__SignTemplatePostEscalationAction_Type {
              signLinkedEvent: linkedEvent {
                id
                release {
                  id
                }
                template {
                  id
                  templateType
                  displayName
                }
              }
            }
            ... on issue__PaymentAgreementPostEscalationAction_Type {
              payLinkedEvent: linkedEvent {
                id
                userAccepted
              }
            }
            ... on issue__BillPayPostEscalationAction_Type {
              billPayLinkedEvent: linkedEvent {
                id
                acceptedAt
                declinedAt
                availableMinimumDiscountOptions
              }
            }
            ... on issue__ConnectInsurancePostEscalationAction_Type {
              insLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
            ... on issue__ConnectEhrPostEscalationAction_Type {
              ehrLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
            ... on issue__PaymentPreferencesPostEscalationAction_Type {
              paymentPrefsLinkedEvent: linkedEvent {
                id
                caseType
                contract
                requestKey
                resolvedFeeCap
                feePercentSnapshot
                cancelledAt
              }
            }
          }
        }
      }
      lastEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
        }
      }
      lastNonUserEvent {
        __typename
        id
        participant
      }
      firstUnseenEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
      }
    }
    me {
      id
      webAgentLinks {
        id
        endpoint {
          ... on webAgent__Endpoint_Type {
          type
          }
        }
        executions(order: { createdAt: { dir: DESC } }, first: 10) {
          id
          createdAt
          signInSucceededAt
          latestStatus
          endpoint {
            ... on webAgent__Endpoint_Type {
              type
            }
          }
        }
      }
    }
  }
`);

/**
 * One row the server named, read with GetIssue's selection so a merged row is
 * complete. The conversation stream names every row it writes, so the client
 * reads exactly those instead of re-reading the transcript. One row per query:
 * Gel's GraphQL rejects a list variable, so `id: { in: $ids }` cannot be sent.
 * A turn can change case state, so one read per batch also selects the issue
 * fields; the rest would only repeat them.
 */
export const getIssueEventByIdQuery = gql(/* GraphQL */ `
  query GetIssueEventById(
    $id: ID!
    $benefitsSummarySchemaVersion: Int!
    $userId: ID!
    $eventId: ID!
    $withIssueFields: Boolean!
  ) {
    issue__Issue(filter: {
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      id
      ... @include(if: $withIssueFields) {
        ...CaseIssueFields @unmask
        ...CaseIssueLatestEvents @unmask
      }
      userVisibleEvents(
        filter: { id: { eq: $eventId } }
        order: { sentAt: { dir: ASC } }
      ) {
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks(filter: { owner: { id: { eq: $userId } } }) {
          id
          rating
        }
        ...CaseDocumentUploadedEventFields @unmask
        ...CaseDocumentClassificationFailedEventFields @unmask
        ...CaseChatEventFields @unmask
        ...CaseHipaaReleaseRequestedEventFields @unmask
        ...CaseBillDisputeEventFields @unmask
        ...CaseTemplateReleaseRequestedEventFields @unmask
        ...CaseClosedEventFields @unmask
        ...CasePaymentAgreementRequestedEventFields @unmask
        ...CaseClosurePaymentRequestedEventFields @unmask
        ...CaseBenefitsSummaryGeneratedEventFields @unmask
        ...CasePlanSelectionEventFields @unmask
        ...CaseUserSelectionRequestedEventFields @unmask
        ...CaseUserMultiSelectionRequestedEventFields @unmask
        ...CaseUserInputRequestedEventFields @unmask
        ...CaseConnectInsuranceRequestedEventFields @unmask
        ...CaseConnectAccountRequestedEventFields @unmask
        ...CaseBillPayRequestedEventFields @unmask
        ...CaseBillPaySuccessEventFields @unmask
        ...CasePaymentPreferencesRequestedEventFields @unmask
        ...CaseCardCollectionRequestedEventFields @unmask
        ...CaseOutreachEventFields @unmask
        ...CaseBillPaymentRequestedEventFields @unmask
        ...CaseBillPaymentSuccessEventFields @unmask
        ...CasePaymentChargeFailedEventFields @unmask
        ...CaseDocumentAnalysisEventFields @unmask
        ...CaseHaAssignedEventFields @unmask
        ...CasePostEscalationActionsEventFields @unmask
      }
    }
  }
`);

export const getIssueEventByIdQueryVariables = (
  issueId: string,
  userId: string,
  eventId: string,
  withIssueFields: boolean,
) => ({ ...getIssueQueryVariables(issueId, userId), eventId, withIssueFields });

/**
 * Everything from the newest row already cached, inclusive so a row sharing
 * its timestamp is not skipped; the merge drops the rows already held. The
 * fallback for when the
 * stream could not name the rows: a dropped event, a reconnect, an update that
 * named none, or a server that predates row ids.
 */
export const getIssueTurnTailQuery = gql(/* GraphQL */ `
  query GetIssueTurnTail(
    $id: ID!
    $benefitsSummarySchemaVersion: Int!
    $userId: ID!
    $after: String!
  ) {
    issue__Issue(filter: {
      id: { eq: $id },
      owner: { id: { eq: $userId } }
    }) {
      ...CaseIssueFields @unmask
      ...CaseIssueLatestEvents @unmask
      userVisibleEvents(
        filter: { sentAt: { gte: $after } }
        order: { sentAt: { dir: ASC } }
      ) {
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks(filter: { owner: { id: { eq: $userId } } }) {
          id
          rating
        }
        ...CaseDocumentUploadedEventFields @unmask
        ...CaseDocumentClassificationFailedEventFields @unmask
        ...CaseChatEventFields @unmask
        ...CaseHipaaReleaseRequestedEventFields @unmask
        ...CaseBillDisputeEventFields @unmask
        ...CaseTemplateReleaseRequestedEventFields @unmask
        ...CaseClosedEventFields @unmask
        ...CasePaymentAgreementRequestedEventFields @unmask
        ...CaseClosurePaymentRequestedEventFields @unmask
        ...CaseBenefitsSummaryGeneratedEventFields @unmask
        ...CasePlanSelectionEventFields @unmask
        ...CaseUserSelectionRequestedEventFields @unmask
        ...CaseUserMultiSelectionRequestedEventFields @unmask
        ...CaseUserInputRequestedEventFields @unmask
        ...CaseConnectInsuranceRequestedEventFields @unmask
        ...CaseConnectAccountRequestedEventFields @unmask
        ...CaseBillPayRequestedEventFields @unmask
        ...CaseBillPaySuccessEventFields @unmask
        ...CasePaymentPreferencesRequestedEventFields @unmask
        ...CaseCardCollectionRequestedEventFields @unmask
        ...CaseOutreachEventFields @unmask
        ...CaseBillPaymentRequestedEventFields @unmask
        ...CaseBillPaymentSuccessEventFields @unmask
        ...CasePaymentChargeFailedEventFields @unmask
        ...CaseDocumentAnalysisEventFields @unmask
        ...CaseHaAssignedEventFields @unmask
        ...CasePostEscalationActionsEventFields @unmask
      }
    }
  }
`);

export const getIssueTurnTailQueryVariables = (
  issueId: string,
  userId: string,
  after: string,
) => ({ ...getIssueQueryVariables(issueId, userId), after });

export const sendMessageMutation = gql(/* GraphQL */ `
  mutation SendMessage($input: CreateIssueChatEventInput!, $userId: ID!) {
    # TODO - ENG-198 - move to a fragment
    createIssueChatEvent(input: $input) {
      __typename
      id
      eventType
      participant
      sentAt
      userVisible
      sentByProfile {
        name
      }
      eventFeedbacks(filter: {owner: {id: {eq: $userId}}}) {
        id
        rating
      }
      ... on issue__ChatEvent_Type {
        chatBody
      }
    }
  }
`);

export const markIssueAsSeenMutation = gql(/* GraphQL */ `
  mutation MarkIssueAsSeen($input: UpdateIssueInput!) {
    updateIssue(input: $input) {
      id
    }
  }
`);

export const selectPlanForEventMutation = gql(/* GraphQL */ `
  mutation selectPlanForEvent($input: SelectPlanForEventInput!) {
    selectPlanForEvent(input: $input) {
      id
    }
  }
`);

// Selects the resolved event so Apollo normalizes it straight off the mutation
// response — the card flips to resolved without a refetch, which would
// otherwise be able to fail after the server had already committed.
export const chargeRetryReadyMutation = gql(/* GraphQL */ `
  mutation chargeRetryReady($input: ChargeRetryReadyInput!) {
    chargeRetryReady(input: $input) {
      id
      userVisibleEvents(order: { sentAt: { dir: ASC } }) {
        __typename
        id
        ... on issue__PaymentChargeFailedEvent_Type {
          paymentResolvedAt
          paymentResolvedReason
        }
      }
    }
  }
`);

export const provideUserInputAnswerMutation = gql(/* GraphQL */ `
  mutation provideUserInputAnswer($input: ProvideUserInputAnswerInput!) {
    provideUserInputAnswer(input: $input) {
      __typename
      id
    }
  }
`);

export const recordIssueChatEventFeedbackMutation = gql(`
  mutation RecordIssueChatEventFeedback($input: RecordIssueEventFeedbackInput!) {
    recordIssueEventFeedback(input: $input) {
      success
    }
  }
`);

export const conversationEventsSubscription = gql(/* GraphQL */ `
  subscription ConversationEvents($issueId: String!) {
    conversationEvents(issueId: $issueId) {
      type
      turnId
      token
      error
      toolName
      documentId
      stage
      attemptId
      attemptStartedAt
      at
      detail
      waitingOn
      waitingOnKind
      waitingOnDocumentId
      seq
      fromSeq
      toSeq
    }
  }
`);

// Adds the row ids. A separate document, so a server without them never sees
// the fields until the streaming v2 flag is on.
export const conversationEventsV2Subscription = gql(/* GraphQL */ `
  subscription ConversationEventsV2($issueId: String!) {
    conversationEvents(issueId: $issueId) {
      type
      turnId
      token
      error
      toolName
      documentId
      stage
      attemptId
      attemptStartedAt
      at
      detail
      waitingOn
      waitingOnKind
      waitingOnDocumentId
      seq
      fromSeq
      toSeq
      eventId
      eventIds
      blockId
      blockRow
    }
  }
`);

export const legacyConversationEventsSubscription = gql(/* GraphQL */ `
  subscription LegacyConversationEvents($issueId: String!) {
    conversationEvents(issueId: $issueId) {
      type
      turnId
      token
      error
      toolName
    }
  }
`);

export const getChatEventFeedbackOptionsQuery = gql(/* GraphQL */ `
  query GetChatEventFeedbackOptions($area: feedback__AreaType!) {
    feedback__Option(
      filter: {
        area: { eq: $area },
        archived: { eq: false }
      },
      order: { order: { dir: ASC } }
    ) {
      id
      value
      sentiment
    }
  }
`);

export const searchProvidersQuery = gql(/* GraphQL */ `
  query SearchProviders($query: String!, $state: String, $limit: Int) {
    searchProviders(query: $query, state: $state, limit: $limit) {
      id
      name
      city
      state
    }
  }
`);

// Resolves a resolved search-answer's stored provider id to its display name.
// The answer event stores only the opaque id; the case's denormalized
// providerName is rewritten by any later setCaseProvider correction, so older
// answer bubbles must resolve their own id to stay historically accurate.
export const providerDisplayNameQuery = gql(/* GraphQL */ `
  query ProviderDisplayName($id: ID!) {
    provider__Provider(filter: { id: { eq: $id } }) {
      id
      displayName
    }
  }
`);
